import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { LiveUpdate } from '@capawesome/capacitor-live-update'
import { toast } from 'sonner'
import { apiClient } from '@/api/client'
import i18n from '@/i18n'
import { trackDeviceEvent } from '@/lib/deviceDiagnostics'
import { AppUpdater } from '@/plugins/app-updater'

/**
 * Live updates of the web bundle on the tablet app (claude/docs/restaurant-app-update-plan-2026-10-20.md, 5.2/5.4).
 * The backend says which bundle this device should run; it is downloaded in the background and
 * applied with a short reload only at a quiet moment, never while orders ring or tickets print.
 * A bundle that does not confirm its start within the plugin's readyTimeout is rolled back.
 */

const CHECK_INTERVAL_MS = 30 * 60_000
const QUIET_CHECK_INTERVAL_MS = 30_000
/** No touch for this long counts as a quiet moment (VITE_APP_UPDATE_QUIET_MS shortens it for test builds). */
export const QUIET_IDLE_MS = Number(import.meta.env.VITE_APP_UPDATE_QUIET_MS) || 10 * 60_000
const LAST_BUNDLE_KEY = 'appUpdate:lastBundle'

interface UpdateResponse {
  channel: string
  release: string | null
  paused: boolean
  bundle: { version: string; sha256: string; minNativeVersionCode: number; url: string } | null
  apk: { versionCode: number; versionName: string; sha256: string; size: number; url: string } | null
  /** iOS: release whose native app waits in the App Store (backends before 1.10.33 do not send it). */
  storeVersion?: string | null
}

export function isLiveUpdateSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() !== 'electron'
}

// Anything that must not be interrupted by a reload registers itself here (ringing orders, print jobs).
const blockers = new Set<string>()

export function setUpdateBlocker(key: string, active: boolean) {
  if (active) blockers.add(key)
  else blockers.delete(key)
}

/** Compares the release part of two bundle versions ("1.10.31+4303" → 1.10.31); negative when a is older. */
export function compareBundleReleases(a: string, b: string): number {
  const parts = (version: string) => version.split('+')[0].split('.').map((part) => Number(part) || 0)
  const [x, y] = [parts(a), parts(b)]
  for (let i = 0; i < 3; i++) {
    const diff = (x[i] ?? 0) - (y[i] ?? 0)
    if (diff !== 0) return diff
  }
  return 0
}

/** Quiet moment: nothing blocks and nobody touched the tablet for a while. */
export function isQuietMoment(now: number, lastInteraction: number, activeBlockers: ReadonlySet<string>): boolean {
  return activeBlockers.size === 0 && now - lastInteraction >= QUIET_IDLE_MS
}

let lastInteraction = Date.now()
let rolledBackAtStart: string | null | undefined

/**
 * Confirms that this bundle started. Must run right after the first render: without it the plugin
 * resets to the previous bundle after readyTimeout, which is the automatic rollback.
 */
export async function confirmBundleStarted() {
  if (!isLiveUpdateSupported()) return
  try {
    const result = await LiveUpdate.ready()
    if (result.rollback) rolledBackAtStart = result.previousBundleId
  } catch (error) {
    console.warn('Live update ready() failed', error)
  }
}

function readLastBundle(): string | null {
  try {
    return localStorage.getItem(LAST_BUNDLE_KEY)
  } catch {
    return null
  }
}

function writeLastBundle(bundle: string) {
  try {
    localStorage.setItem(LAST_BUNDLE_KEY, bundle)
  } catch {
    // Without storage the "updated" notice is just skipped.
  }
}

function errorText(error: unknown): string {
  return (error instanceof Error ? error.message : String(error)).slice(0, 300)
}

interface PendingApk {
  versionCode: number
  versionName: string
  path: string
}

/** What the Tablet page shows: an update can also be checked for and installed by hand. */
export interface AppUpdateStatus {
  checking: boolean
  /** Version name of a downloaded APK that waits for its install. */
  apkReady: string | null
  /** A downloaded web update that applies with a short reload. */
  bundleReady: boolean
  failed: boolean
  /** Store installs (Play build, iOS): version name of a newer native app waiting in the store. */
  storeUpdate: string | null
}

let status: AppUpdateStatus = { checking: false, apkReady: null, bundleReady: false, failed: false, storeUpdate: null }
const statusListeners = new Set<() => void>()

function setStatus(change: Partial<AppUpdateStatus>) {
  status = { ...status, ...change }
  statusListeners.forEach((listener) => listener())
}

export function subscribeUpdateStatus(listener: () => void): () => void {
  statusListeners.add(listener)
  return () => statusListeners.delete(listener)
}

export function getUpdateStatus(): AppUpdateStatus {
  return status
}

// Set while update checks run (signed-in tablet); the Tablet page calls them on demand.
let checkNow: (() => Promise<void>) | null = null
let applyBundleNow: (() => Promise<void>) | null = null

export async function checkForUpdatesNow() {
  await checkNow?.()
}

/** "Update now" on the Tablet page: the APK if one waits (it brings a newer web part too), else the web update. */
export async function applyUpdateNow() {
  if (pendingApk) await installPendingApk(true)
  else await applyBundleNow?.()
}

const STORE_NOTICE_KEY = 'appUpdate:storeNotice'

/** Opens the app's store page; Capacitor hands the external URL to the Play Store or App Store app. */
export async function openAppStore() {
  const ios = Capacitor.getPlatform() === 'ios'
  const id = ios ? null : (await CapacitorApp.getInfo().catch(() => null))?.id
  window.location.href = ios
    ? 'https://apps.apple.com/app/id6825784727'
    : `https://play.google.com/store/apps/details?id=${id ?? 'com.leckerorder.admin'}`
}

/** Store installs cannot install an APK themselves: they get a notice once per version, the Tablet page keeps showing it. */
function noticeStoreUpdate(versionName: string) {
  if (status.storeUpdate === versionName) return
  setStatus({ storeUpdate: versionName })
  trackDeviceEvent('update.available', { store: versionName })
  try {
    if (localStorage.getItem(STORE_NOTICE_KEY) === versionName) return
    localStorage.setItem(STORE_NOTICE_KEY, versionName)
  } catch {
    // Without storage the notice just shows on every start.
  }
  const store = Capacitor.getPlatform() === 'ios' ? 'App Store' : 'Play Store'
  toast(i18n.t('common:storeUpdate.title', { version: versionName, store }), {
    duration: 15_000,
    action: { label: i18n.t('common:storeUpdate.open'), onClick: () => void openAppStore() },
  })
}

let pendingApk: PendingApk | null = null
let apkPromptListener: ((versionCode: number | null) => void) | null = null
let apkPromptSnoozedUntil = 0
const APK_PROMPT_SNOOZE_MS = 60 * 60_000

/** The update banner subscribes here; it is told when an APK waits for a tap (Android ≤ 11 or missing permission). */
export function subscribeApkPrompt(listener: (versionCode: number | null) => void): () => void {
  apkPromptListener = listener
  return () => {
    if (apkPromptListener === listener) apkPromptListener = null
  }
}

export function snoozeApkPrompt() {
  apkPromptSnoozedUntil = Date.now() + APK_PROMPT_SNOOZE_MS
  apkPromptListener?.(null)
}

/**
 * Installs the downloaded APK; Android replaces the app and restarts it (AppRestartReceiver).
 * Interactive (banner tap) may show Android's dialog; the automatic attempt never covers the screen.
 */
export async function installPendingApk(interactive = true) {
  if (!pendingApk) return
  apkPromptListener?.(null)
  try {
    trackDeviceEvent('update.installing', { apk: pendingApk.versionCode, interactive })
    await AppUpdater.install({ path: pendingApk.path, interactive })
  } catch (error) {
    trackDeviceEvent('update.failed', { stage: 'install', error: errorText(error) }, 'warn')
  }
}

/** Starts update checks for a signed-in restaurant tablet; returns a stop function. */
export async function startAppUpdates(restaurantId: number): Promise<() => void> {
  if (!isLiveUpdateSupported()) return () => undefined

  const current = __BUNDLE_VERSION__
  const previous = readLastBundle()
  if (rolledBackAtStart !== undefined) {
    trackDeviceEvent('update.rollback', { bundle: rolledBackAtStart }, 'warn')
    rolledBackAtStart = undefined
  } else if (previous && previous !== current) {
    trackDeviceEvent('update.applied', { from: previous, to: current })
    toast.success(i18n.t('common:appUpdated', { version: __APP_VERSION__ }))
  }
  writeLastBundle(current)

  const info = await CapacitorApp.getInfo().catch(() => null)
  const nativeVersionCode = Number(info?.build ?? 0) || 0
  // Native updates of store installs come from the store; only tablet APKs ("direct") update themselves.
  const storeInstall =
    Capacitor.getPlatform() === 'ios' ||
    (Capacitor.getPlatform() === 'android' && (await AppUpdater.getState().catch(() => null))?.distribution === 'play')
  let pendingBundle: string | null = null
  let silentAttempt: number | null = null
  let checking = false
  let stopped = false

  const check = async () => {
    if (checking || stopped) return
    checking = true
    let failed = false
    setStatus({ checking: true })
    try {
      const { data } = await apiClient.get<UpdateResponse>(`/api/admin/restaurants/${String(restaurantId)}/app-update`, {
        params: { native: nativeVersionCode, bundle: current, platform: Capacitor.getPlatform() },
      })
      const apk = data.apk
      if (data.storeVersion) {
        noticeStoreUpdate(data.storeVersion)
      } else if (apk && apk.versionCode > nativeVersionCode && storeInstall) {
        noticeStoreUpdate(apk.versionName)
      } else if (apk && apk.versionCode > nativeVersionCode && Capacitor.getPlatform() === 'android' && pendingApk?.versionCode !== apk.versionCode) {
        trackDeviceEvent('update.available', { apk: apk.versionCode })
        const startedAt = Date.now()
        const { path } = await AppUpdater.download({ url: apk.url, sha256: apk.sha256 })
        pendingApk = { versionCode: apk.versionCode, versionName: apk.versionName, path }
        trackDeviceEvent('update.downloaded', { apk: apk.versionCode, ms: Date.now() - startedAt })
      }
      const bundle = data.bundle
      if (!bundle || bundle.version === pendingBundle) return
      // Tablets follow their release both ways, so moving a restaurant back is the rollback. Store installs
      // never go below the web part their store version shipped with.
      if (storeInstall && info?.version && compareBundleReleases(bundle.version, info.version) < 0) return
      // A bundle that already failed to start on this device is never applied again (no rollback loop).
      const { bundleIds: blocked } = await LiveUpdate.getBlockedBundles()
      if (blocked.includes(bundle.version)) return
      trackDeviceEvent('update.available', { bundle: bundle.version })
      const { bundleIds } = await LiveUpdate.getDownloadedBundles()
      if (!bundleIds.includes(bundle.version)) {
        const startedAt = Date.now()
        await LiveUpdate.downloadBundle({ bundleId: bundle.version, url: bundle.url, checksum: bundle.sha256 })
        trackDeviceEvent('update.downloaded', { bundle: bundle.version, ms: Date.now() - startedAt })
      }
      // Takes effect on the next start by itself; the quiet-moment loop below applies it sooner.
      await LiveUpdate.setNextBundle({ bundleId: bundle.version })
      pendingBundle = bundle.version
    } catch (error) {
      failed = true
      trackDeviceEvent('update.failed', { stage: 'check', error: errorText(error) }, 'warn')
    } finally {
      checking = false
      setStatus({ checking: false, failed, apkReady: pendingApk?.versionName ?? null, bundleReady: pendingBundle !== null })
    }
  }
  checkNow = check
  applyBundleNow = async () => {
    if (pendingBundle) await LiveUpdate.reload()
  }

  const onInteraction = () => {
    lastInteraction = Date.now()
  }
  const hidePromptOnInteraction = (event: PointerEvent) => {
    if (!(event.target instanceof Element && event.target.closest('[data-app-update-banner]'))) apkPromptListener?.(null)
  }
  window.addEventListener('pointerdown', onInteraction, { passive: true })
  // Someone is busy at the tablet again: the update banner waits for the next quiet moment.
  window.addEventListener('pointerdown', hidePromptOnInteraction, { passive: true })
  window.addEventListener('keydown', onInteraction, { passive: true })

  const quietTimer = window.setInterval(() => {
    if (!isQuietMoment(Date.now(), lastInteraction, blockers)) return
    // A new APK comes first: it restarts the app anyway and brings its own, newer built-in bundle.
    if (pendingApk) {
      const apk = pendingApk
      void AppUpdater.getState().then((state) => {
        // One silent attempt per APK; if the app is still running afterwards, it needed a confirmation.
        if (state.canInstallSilently && !state.silentInstallBlocked && silentAttempt !== apk.versionCode) {
          silentAttempt = apk.versionCode
          void installPendingApk(false)
        } else if (Date.now() >= apkPromptSnoozedUntil) {
          apkPromptListener?.(apk.versionCode)
        }
      })
      return
    }
    if (!pendingBundle) return
    LiveUpdate.reload().catch((error: unknown) => {
      trackDeviceEvent('update.failed', { stage: 'apply', error: errorText(error) }, 'warn')
    })
  }, QUIET_CHECK_INTERVAL_MS)
  const checkTimer = window.setInterval(() => void check(), CHECK_INTERVAL_MS)
  const resumeListener = await CapacitorApp.addListener('resume', () => void check())
  void check()

  return () => {
    stopped = true
    checkNow = null
    applyBundleNow = null
    window.clearInterval(quietTimer)
    window.clearInterval(checkTimer)
    window.removeEventListener('pointerdown', onInteraction)
    window.removeEventListener('pointerdown', hidePromptOnInteraction)
    window.removeEventListener('keydown', onInteraction)
    void resumeListener.remove()
  }
}
